// DS3.10, DS3.10M
// Note that DS3.10M is achieved using `layout='vertical'`
import React from 'react';
import isEqual from 'lodash/isEqual';
import VerticalLayout from './layouts/VerticalLayout';
import HorizontalLayout from './layouts/HorizontalLayout';
import LargeInputFieldButton, {
  type LargeInputFieldButtonProps,
} from './lego/LargeInputFieldButton';
import ClearButton from './lego/ClearButton';
import LargeInputField, {
  type LargeInputFieldProps,
} from './lego/LargeInputField';

type Layout = 'vertical' | 'horizontal';

export interface Props
  extends Omit<LargeInputFieldProps, 'postfix' | 'className' | 'isPostfixed'> {
  /**
   * Organizes the input field and button according to the passed layout.
   */
  layout: Layout;

  /**
   * A list of props that get passed to the internal `LargeInputFieldButton` component.
   */
  buttonProps: Omit<LargeInputFieldButtonProps, 'isPostfix'>;

  className?: string;

  /**
   * If provided, we'll add a clear button to the end of the input. You're responsible
   * for the clearing logic.
   */
  onClear?: () => void;
}

function getRootLayoutComponent(layout: Layout) {
  switch (layout) {
    case 'horizontal':
      return HorizontalLayout;
    case 'vertical':
    default:
      return VerticalLayout;
  }
}

/**
 * An input field with a button. This provides both horizontal and vertical layouts.
 */
const LargeInputFieldWithButton: React.FC<Props> = ({
  value,
  layout,
  onClear,
  className,
  buttonProps,
  ...rest
}) => {
  const RootLayout = getRootLayoutComponent(layout);
  return (
    <RootLayout className={className}>
      <LargeInputField
        isPostfixed={layout === 'horizontal'}
        value={value}
        postfix={value && onClear && <ClearButton onClick={onClear} />}
        {...rest}
      />
      <LargeInputFieldButton
        isPostfix={layout === 'horizontal'}
        {...buttonProps}
      />
    </RootLayout>
  );
};

LargeInputFieldWithButton.displayName = 'LargeInputField';

// Since we accept a `buttonProps` object, running the passed props through lodash's `isEqual` function.
// That should cut down on unnecessary rerenders.
export default React.memo(LargeInputFieldWithButton, isEqual);
